import React from 'react';
import { createPortal } from 'react-dom';

const ToastDurationMs = 2400;

export type AdminToastTone = 'success' | 'warning' | 'critical' | 'info';

export interface AdminToastOptions {
  tone?: AdminToastTone;
}

interface AdminToastState {
  id: number;
  message: string;
  tone: AdminToastTone;
}

interface AdminToastContextValue {
  showToast: (message: string, options?: AdminToastOptions) => void;
  dismissToast: () => void;
}

const AdminToastContext = React.createContext<AdminToastContextValue | null>(null);

export const useAdminToast = (): AdminToastContextValue => {
  const context = React.useContext(AdminToastContext);
  if (!context) {
    throw new Error('useAdminToast must be used within AdminToastProvider.');
  }
  return context;
};

export const AdminToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [toast, setToast] = React.useState<AdminToastState | null>(null);
  const nextId = React.useRef(0);
  const dismissToast = React.useCallback(() => setToast(null), []);
  const showToast = React.useCallback((message: string, options: AdminToastOptions = {}) => {
    if (message.trim() === '') {
      return;
    }
    nextId.current += 1;
    setToast({ id: nextId.current, message, tone: options.tone ?? 'success' });
  }, []);

  React.useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timeout = window.setTimeout(dismissToast, ToastDurationMs);
    return () => window.clearTimeout(timeout);
  }, [dismissToast, toast]);

  const context = React.useMemo(
    () => ({ showToast, dismissToast }),
    [dismissToast, showToast]
  );
  const toastElement = toast ? (
    <div className={`pipeex-admin-portal admin-toast admin-toast--${toast.tone}`} role="status" aria-live="polite" aria-atomic="true">
      <span className="admin-toast__dot" aria-hidden="true" />
      {toast.message}
    </div>
  ) : null;

  return (
    <AdminToastContext.Provider value={context}>
      {children}
      {toastElement && typeof document !== 'undefined' ? createPortal(toastElement, document.body) : toastElement}
    </AdminToastContext.Provider>
  );
};
